import React from 'react';
import { Link } from 'react-router-dom';
import GuideLayout from '../common/GuideLayout';
import GuideCta from '../common/GuideCta';
import RelatedGuides from '../common/RelatedGuides';
import { RSVP_CLUSTER } from '../common/rsvpCluster';
import { SITE_SIGNUP_URL } from '../signup/startIntent';
import { CASES, COLUMNS, FAQ, READING_MINUTES, STEPS, TEMPLATE_URL, TOC, UPDATED } from './content';

// Bouton de téléchargement du modèle Excel (fichier statique)
const DownloadTemplate: React.FC = () => (
  <a href={TEMPLATE_URL} download className="mkt-btn primary">
    Télécharger le modèle Excel
  </a>
);

// Page « Liste d'invités mariage » : modèle Excel gratuit (importable dans l'app),
// méthode, nombre d'invités, cas délicats ; appels à passer à la liste en ligne.
const GuestListGuidePage: React.FC = () => (
  <GuideLayout
    eyebrow="Guide · Invités"
    title="Liste d’invités de mariage : modèle Excel gratuit et méthode"
    updated={UPDATED}
    published="2026-10-15"
    readingMinutes={READING_MINUTES}
    lead="Faire la liste des invités, c’est souvent la première vraie décision du mariage. Un modèle Excel gratuit à télécharger, une méthode en 5 étapes pour arriver au bon nombre, et des repères pour les cas délicats : +1, enfants, collègues, liste B."
    topCta={
      <aside className="mg-cta-box">
        <div>
          <p className="mg-cta-title">Le modèle de liste d’invités, gratuit</p>
          <p>Un fichier Excel prêt à remplir : une ligne par invité, une colonne par moment de la journée.</p>
        </div>
        <DownloadTemplate />
      </aside>
    }
    toc={TOC}
    faq={FAQ}
  >
    <section id="modele">
      <h2>Le modèle Excel gratuit de liste d’invités</h2>
      <p>
        Une ligne par personne, enfants compris, et une colonne par moment de la journée : en un coup d’œil, vous savez
        qui est invité à quoi. Les colonnes du modèle :
      </p>
      <div className="mg-table-scroll">
        <table className="mg-table mg-compare">
          <thead>
            <tr>
              <th scope="col">Colonne</th>
              <th scope="col">À quoi elle sert</th>
            </tr>
          </thead>
          <tbody>
            {COLUMNS.map(([name, use]) => (
              <tr key={name}>
                <th scope="row">{name}</th>
                <td>{use}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        Il s’ouvre dans Excel, Numbers, LibreOffice ou Google Sheets. Renommez les colonnes de moments selon votre
        mariage (mairie, cocktail, soirée…), et ajoutez-en si besoin.
      </p>
      <div className="mg-download">
        <DownloadTemplate />
        <span>Fichier .xlsx · gratuit, sans inscription</span>
      </div>
    </section>

    <section id="methode">
      <h2>Faire sa liste d’invités en 5 étapes</h2>
      <ol className="mg-steps">
        {STEPS.map((s) => (
          <li key={s.title}>
            <h3>{s.title}</h3>
            <p>{s.text}</p>
          </li>
        ))}
      </ol>
    </section>

    <section id="combien">
      <h2>Combien d’invités pour un mariage ?</h2>
      <p>
        En France, un mariage réunit en moyenne <b>autour de 90 invités</b> (Rapport sur le secteur nuptial 2026 de
        Mariages.net). Mais le bon nombre dépend surtout de deux limites : la capacité de votre lieu, et votre budget.
        Comptez environ 215 € par invité en moyenne : chaque personne de plus pèse sur le traiteur, les boissons et le
        reste. Voir notre guide du <Link to="/budget-mariage">budget de mariage par nombre d’invités</Link>.
      </p>
    </section>

    <section id="cas">
      <h2>Familles, enfants, +1 : les cas délicats</h2>
      <div className="mg-grid">
        {CASES.map((c) => (
          <div key={c.title} className="mg-card">
            <h3>{c.title}</h3>
            <p>{c.text}</p>
          </div>
        ))}
      </div>
    </section>

    <section id="en-ligne">
      <h2>Du tableur à la liste en ligne</h2>
      <p>
        Le tableur suffit pour décider qui inviter. C’est ensuite qu’il montre ses limites : les réponses arrivent par
        SMS, par email ou de vive voix, et il faut tout recopier, compter, relancer.
      </p>
      <p>
        Avec RSVP Please, vous <b>importez ce modèle tel quel</b> : prénoms, noms, enfants, téléphones et invitations à
        chaque moment sont repris (les colonnes Groupe et Notes restent dans votre fichier). Vos invités répondent
        ensuite en ligne, et chaque réponse s’inscrit dans la liste, avec des compteurs par événement. Pour la suite,
        voir notre guide du <Link to="/rsvp-mariage">RSVP en ligne</Link>.
      </p>
      <GuideCta
        to={SITE_SIGNUP_URL}
        title="Votre liste d’invités, en ligne et à jour toute seule"
        text="Importez votre fichier Excel, partagez votre page : présents, absents et en attente se comptent tout seuls."
        label="Importer ma liste gratuitement"
      />
    </section>

    <RelatedGuides items={[RSVP_CLUSTER.rsvp, RSVP_CLUSTER.form, RSVP_CLUSTER.qr]} />
  </GuideLayout>
);

export default GuestListGuidePage;
